import { useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Grid2 as Grid, Alert, FormControlLabel, Switch, CircularProgress,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { vendorsApi, type VendorDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";

interface VendorFormProps {
  open: boolean;
  onClose: () => void;
  vendor?: VendorDto | null;
}

interface FormData {
  code: string;
  name: string;
  gstin?: string;
  pan?: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  paymentTerms?: string;
  creditDays?: number | null;
  isActive: boolean;
}

const empty: FormData = {
  code: "", name: "",
  gstin: "", pan: "", contactPerson: "", phone: "", email: "",
  paymentTerms: "", creditDays: null, isActive: true,
};

export function VendorForm({ open, onClose, vendor }: VendorFormProps) {
  const qc = useQueryClient();
  const isEdit = !!vendor;
  const { control, handleSubmit, reset, formState: { errors, isSubmitting } } =
    useForm<FormData>({ defaultValues: empty });

  useEffect(() => {
    reset(vendor ? {
      code: vendor.code,
      name: vendor.name,
      gstin: vendor.gstin ?? "",
      pan: vendor.pan ?? "",
      contactPerson: vendor.contactPerson ?? "",
      phone: vendor.phone ?? "",
      email: vendor.email ?? "",
      paymentTerms: vendor.paymentTerms ?? "",
      creditDays: vendor.creditDays ?? null,
      isActive: vendor.isActive,
    } : empty);
  }, [vendor, open, reset]);

  const mutation = useMutation({
    meta: { successMessage: isEdit ? "Vendor updated" : "Vendor created" },
    mutationFn: async (data: FormData) => {
      const payload = {
        ...data,
        gstin: data.gstin || undefined,
        pan: data.pan || undefined,
        email: data.email || undefined,
        phone: data.phone || undefined,
        contactPerson: data.contactPerson || undefined,
        paymentTerms: data.paymentTerms || undefined,
        creditDays: data.creditDays ?? undefined,
      };
      if (isEdit && vendor) {
        await vendorsApi.update(vendor.id, payload);
        return vendor.id;
      }
      const result = await vendorsApi.create(payload);
      return result.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendors"] });
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit((d) => mutation.mutate(d))}>
        <DialogTitle>{isEdit ? "Edit vendor" : "New vendor"}</DialogTitle>
        <DialogContent dividers>
          {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller
                name="code" control={control}
                rules={{ required: "Required", maxLength: 50 }}
                render={({ field }) => (
                  <TextField {...field} label="Code *" fullWidth size="small" disabled={isEdit}
                    error={!!errors.code} helperText={errors.code?.message} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Controller
                name="name" control={control}
                rules={{ required: "Required", maxLength: 200 }}
                render={({ field }) => (
                  <TextField {...field} label="Name *" fullWidth size="small"
                    error={!!errors.name} helperText={errors.name?.message} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="gstin" control={control}
                rules={{
                  pattern: {
                    value: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
                    message: "Invalid GSTIN format"
                  }
                }}
                render={({ field }) => (
                  <TextField {...field} label="GSTIN" fullWidth size="small"
                    error={!!errors.gstin} helperText={errors.gstin?.message ?? "15-char GSTIN"} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="pan" control={control}
                rules={{
                  pattern: {
                    value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                    message: "PAN must be 10 characters (e.g. ABCDE1234F)"
                  }
                }}
                render={({ field }) => (
                  <TextField {...field}
                    label="PAN" fullWidth size="small"
                    onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                    error={!!errors.pan} helperText={errors.pan?.message ?? "Optional · 10-char PAN"} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="contactPerson" control={control}
                render={({ field }) => <TextField {...field} label="Contact person" fullWidth size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Controller name="phone" control={control}
                render={({ field }) => <TextField {...field} label="Phone" fullWidth size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Controller name="email" control={control}
                rules={{ pattern: { value: /^\S+@\S+\.\S+$/, message: "Invalid email" } }}
                render={({ field }) => (
                  <TextField {...field} type="email" label="Email" fullWidth size="small"
                    error={!!errors.email} helperText={errors.email?.message} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="paymentTerms" control={control}
                render={({ field }) => (
                  <TextField {...field} label="Payment terms" fullWidth size="small"
                    placeholder="e.g. Net 30" />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Controller name="creditDays" control={control}
                rules={{ min: { value: 0, message: "Must be >= 0" } }}
                render={({ field }) => (
                  <TextField {...field} type="number" label="Credit days" fullWidth size="small"
                    onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))}
                    value={field.value ?? ""}
                    error={!!errors.creditDays} helperText={errors.creditDays?.message} />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Controller name="isActive" control={control}
                render={({ field }) => (
                  <FormControlLabel
                    label="Active"
                    control={<Switch checked={field.value} onChange={(_, v) => field.onChange(v)} />}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button type="submit" variant="contained"
            disabled={isSubmitting || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            {isEdit ? "Save changes" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
